import React, { useMemo } from 'react';
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    ReferenceLine,
    ReferenceArea
} from 'recharts';
import { Volume2, BarChart3, Waves } from 'lucide-react';
import styles from './PitchAnalysis.module.css';
import InfoModal from '../InfoModal/InfoModal';

const PitchAnalysis = ({ environment, ranges, segments }) => {
    const { pitchData, stats, recommendedRange, graphYAxis } = useMemo(() => {
        if (!segments?.length) return {
            pitchData: [],
            stats: {},
            recommendedRange: null,
            graphYAxis: { min: 0, max: 200 }
        };

        const data = segments
            .filter(segment => segment.type === 'word' && segment.pitch)
            .map((segment, index) => ({
                word: segment.content,
                pitch: segment.pitch,
                index,
                timestamp: segment.timestamp,
                tooltipContent: `${segment.content} (${segment.pitch.toFixed(1)} Hz)`
            }));

        const pitches = data.map(d => d.pitch);
        const avgPitch = pitches.reduce((sum, pitch) => sum + pitch, 0) / pitches.length;
        const maxPitch = Math.max(...pitches);
        const minPitch = Math.min(...pitches);
        const pitchRange = maxPitch - minPitch;

        // Determine recommended pitch range
        const recommendedRange = ranges?.pitch
            ? {
                min: ranges.pitch[0],
                max: ranges.pitch[1]
            }
            : {
                min: 120,
                max: 180
            };

        // Calculate graph Y-axis range
        const graphMin = Math.min(minPitch, recommendedRange.min);
        const graphMax = Math.max(maxPitch, recommendedRange.max);
        const padding = (graphMax - graphMin) * 0.1; // 10% padding

        return {
            pitchData: data,
            stats: {
                averagePitch: avgPitch,
                maxPitch,
                minPitch,
                pitchRange
            },
            recommendedRange,
            graphYAxis: {
                min: Math.max(0, Math.floor(graphMin - padding)),
                max: Math.ceil(graphMax + padding)
            }
        };
    }, [segments, ranges]);

    if (!segments?.length) return null;

    const CustomTooltip = ({ active, payload }) => {
        if (!active || !payload?.[0]) return null;

        return (
            <div className={styles.customTooltip}>
                <p className={styles.tooltipText}>{payload[0].payload.tooltipContent}</p>
            </div>
        );
    };

    const MetricCard = ({ icon, label, value, unit = '' }) => (
        <div className={styles.metricCard}>
            {icon}
            <div className={styles.metricContent}>
                <span className={styles.metricLabel}>{label}</span>
                <span className={styles.metricValue}>
                    {value}{unit}
                </span>
            </div>
        </div>
    );

    return (
        <div className={styles.analysisSection}>
            <div className={styles.titleContainer}>
                <InfoModal type="pitch" environment={environment} ranges={ranges} />
            </div>
            <div className={styles.metricsGrid}>
                <MetricCard
                    icon={<Volume2 className={styles.metricIcon} />}
                    label="Average Pitch"
                    value={stats.averagePitch.toFixed(1)}
                    unit=" Hz"
                />
                <MetricCard
                    icon={<Waves className={styles.metricIcon} />}
                    label="Pitch Range"
                    value={stats.pitchRange.toFixed(1)}
                    unit=" Hz"
                />
                <MetricCard
                    icon={<BarChart3 className={styles.metricIcon} />}
                    label="Variation Score"
                    value={(stats.pitchRange / stats.averagePitch * 100).toFixed(1)}
                    unit="%"
                />
            </div>

            <div className={styles.chartSection}>
                <ResponsiveContainer width="100%" height={300}>
                    <AreaChart
                        data={pitchData}
                        margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                    >
                        <defs>
                            <linearGradient id="pitchGradient" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#6366f1" stopOpacity={0.8} />
                                <stop offset="95%" stopColor="#818cf8" stopOpacity={0.2} />
                            </linearGradient>
                            <linearGradient id="recommendedRangeGradient" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                                <stop offset="95%" stopColor="#10b981" stopOpacity={0.1} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis
                            dataKey="index"
                            label={{
                                value: 'Word Position',
                                position: 'bottom',
                                offset: -10,
                                style: { fill: '#64748b' }
                            }}
                            tick={{ fill: '#64748b' }}
                        />
                        <YAxis
                            domain={[graphYAxis.min, graphYAxis.max]}
                            dataKey="pitch"
                            tickFormatter={(value) => `${Math.round(value)}`}
                            label={{
                                value: 'Pitch (Hz)',
                                angle: -90,
                                position: 'insideLeft',
                                offset: 10,
                                style: { fill: '#64748b' }
                            }}
                            tick={{ fill: '#64748b' }}
                        />
                        <Tooltip content={<CustomTooltip />} />

                        {/* Recommended Range Area */}
                        <ReferenceArea
                            y1={Math.max(graphYAxis.min, recommendedRange.min)}
                            y2={Math.min(graphYAxis.max, recommendedRange.max)}
                            stroke="#10b981"
                            strokeOpacity={0.4}
                            fill="url(#recommendedRangeGradient)"
                            label={{
                                value: "Recommended Range",
                                position: 'insideTopRight',
                                fill: '#10b981'
                            }}
                        />

                        {/* Average Pitch Line */}
                        <ReferenceLine
                            y={stats.averagePitch}
                            stroke="#f59e0b"
                            strokeDasharray="3 3"
                            label={{
                                value: "Average pitch",
                                position: 'right',
                                fill: '#f59e0b'
                            }}
                        />

                        {/* Actual Pitch Area */}
                        <Area
                            type="monotone"
                            dataKey="pitch"
                            stroke="#6366f1"
                            strokeWidth={2}
                            fill="url(#pitchGradient)"
                        />
                    </AreaChart>
                </ResponsiveContainer>
            </div>

            <div className={styles.legend}>
                <div className={styles.legendItem}>
                    <span className={styles.pitchGradient}></span>
                    <span>Pitch pattern</span>
                </div>
                <div className={styles.legendItem}>
                    <span className={styles.recommendedLine}></span>
                    <span>Recommended range</span>
                </div>
                <div className={styles.legendItem}>
                    <span className={styles.thresholdLine}></span>
                    <span>Your Average pitch</span>
                </div>
            </div>
        </div>
    );
};

export default PitchAnalysis;